/**
 * page.tsx — "Como instalar o app no seu celular".
 *
 *   /public/instalar   — sem login de propósito: o link vai por WhatsApp e a
 *                         pessoa instala ANTES de ter usuário e senha em mãos.
 *
 * O PROBLEMA QUE ISTO RESOLVE. Instalar é passo manual e diferente em cada
 * aparelho: no Android o Chrome oferece um botão; no iPhone é Compartilhar →
 * "Adicionar à Tela de Início", e só pelo SAFARI — o Chrome do iOS não instala
 * nada, por decisão da Apple. Explicar isso por mensagem, para uma equipe com
 * celulares diferentes, é onde a instalação morre: as pessoas continuam abrindo
 * pelo endereço, com a barra do navegador na frente, e concluem que "não
 * funciona no meu".
 *
 * A tela lê o aparelho e mostra UM caminho — o certo — em vez de uma lista de
 * instruções onde a pessoa precisa se encontrar.
 *
 * PARA QUE SERVE INSTALAR, já que o site funciona igual: ícone na tela inicial
 * (abre em um toque, sem digitar endereço) e tela cheia, sem a barra do
 * navegador. Quem abre o app no meio da rua, entre uma instalação e outra,
 * sente essa diferença.
 *
 * Portado do projeto irmão `webapp-rm-ops` (08/08/2026).
 */
import { useState } from 'react';
import { instalarAgora } from '@/lib/instalacao';
import { usarInstalacao } from '@/lib/usar-instalacao';

export function InstalarPage() {
  const { ambiente: amb, temPrompt } = usarInstalacao();
  const [instalando, setInstalando] = useState(false);
  const [aceitou, setAceitou] = useState(false);

  if (!amb) return null;

  async function instalar() {
    setInstalando(true);
    const ok = await instalarAgora();
    setAceitou(ok);
    setInstalando(false);
  }

  return (
    <div className="container py-4" style={{ maxWidth: 560 }}>
      <div className="text-center mb-4">
        <img src="/icone-192-v2.png" alt="" width={72} height={72} className="rounded-4 mb-2" />
        <h4 className="mb-1">
          {amb.aparelho === 'desktop' ? 'Instalar o app' : 'Instalar no celular'}
        </h4>
        <p className="text-secondary small mb-0">
          {amb.aparelho === 'desktop'
            ? 'O app ganha uma janela própria, sem a barra do navegador.'
            : 'Fica um ícone na tela inicial e o app abre em tela cheia, sem a barra do navegador.'}
        </p>
      </div>

      {/* ── já resolvido ──────────────────────────────────────────────── */}
      {(amb.instalado || aceitou) && (
        <div className="card adminuiux-card border-success">
          <div className="card-body text-center py-5">
            <i className="bi bi-check-circle display-6 text-success"></i>
            <h5 className="mt-3 mb-1">Está instalado!</h5>
            <p className="text-secondary small mb-0">
              Procure o ícone da <strong>Casa A</strong>{' '}
              {amb.aparelho === 'desktop'
                ? 'entre os seus programas e abra por ele daqui em diante.'
                : 'na tela do seu celular e abra por ele daqui em diante.'}
            </p>
          </div>
        </div>
      )}

      {!amb.instalado && !aceitou && (
        <>
          {/* ── dentro do WhatsApp: nada funciona daqui ────────────────── */}
          {amb.dentroDeApp && (
            <div className="alert alert-warning">
              <div className="fw-medium mb-1">
                <i className="bi bi-exclamation-triangle me-1"></i>
                Você abriu pelo WhatsApp
              </div>
              <p className="small mb-2">
                Não dá para instalar por aqui. Toque nos{' '}
                <strong>três pontinhos</strong> no canto da tela e escolha{' '}
                <strong>
                  {amb.aparelho === 'ios' ? 'Abrir no Safari' : 'Abrir no Chrome'}
                </strong>
                . Depois volte a esta página.
              </p>
              <button
                className="btn btn-sm btn-dark"
                type="button"
                onClick={() => {
                  navigator.clipboard?.writeText(window.location.href);
                }}
              >
                Copiar o endereço
              </button>
            </div>
          )}

          {/* ── Android com Chrome: botão de verdade ───────────────────── */}
          {amb.aparelho === 'android' && !amb.dentroDeApp && (
            <div className="card adminuiux-card">
              <div className="card-body">
                {temPrompt ? (
                  <>
                    <p className="mb-3">
                      É um toque só. O celular vai perguntar se pode instalar —
                      responda que sim.
                    </p>
                    <button
                      className="btn btn-theme btn-lg w-100"
                      type="button"
                      onClick={instalar}
                      disabled={instalando}
                    >
                      <i className="bi bi-download me-2"></i>
                      {instalando ? 'Instalando…' : 'Instalar agora'}
                    </button>
                  </>
                ) : (
                  <>
                    {/* Sem o evento do Chrome: ou já foi instalado, ou o
                        navegador não é o Chrome. O caminho manual sempre
                        existe. */}
                    <p className="mb-2">Pelo menu do navegador:</p>
                    <Passos
                      itens={[
                        <>
                          Toque nos <strong>três pontinhos</strong> (⋮) no canto
                          de cima
                        </>,
                        <>
                          Escolha <strong>Instalar aplicativo</strong> ou{' '}
                          <strong>Adicionar à tela inicial</strong>
                        </>,
                        <>Confirme</>,
                      ]}
                    />
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── iPhone: depende do Safari ──────────────────────────────── */}
          {amb.aparelho === 'ios' && !amb.dentroDeApp && (
            <div className="card adminuiux-card">
              <div className="card-body">
                {amb.navegador !== 'safari' ? (
                  <div className="alert alert-warning mb-0">
                    <div className="fw-medium mb-1">
                      No iPhone, só o Safari instala
                    </div>
                    <p className="small mb-0">
                      Você está em outro navegador. Copie este endereço, abra o{' '}
                      <strong>Safari</strong> e cole lá — depois volte a esta
                      página para ver o passo a passo.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="mb-2">No Safari, é assim:</p>
                    <Passos
                      itens={[
                        <>
                          Toque no botão <strong>Compartilhar</strong>{' '}
                          <i className="bi bi-box-arrow-up"></i> — fica na barra
                          de baixo
                        </>,
                        <>
                          Deslize a lista e escolha{' '}
                          <strong>Adicionar à Tela de Início</strong>
                        </>,
                        <>
                          Toque em <strong>Adicionar</strong>, no canto de cima
                        </>,
                      ]}
                    />
                    <p className="small text-secondary mb-0">
                      O ícone da Casa A aparece junto com os outros apps.
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── computador ─────────────────────────────────────────────── */}
          {amb.aparelho === 'desktop' && (
            <div className="card adminuiux-card">
              <div className="card-body">
                {temPrompt ? (
                  <>
                    <p className="mb-3">
                      É um clique. O app ganha uma janela própria e um atalho
                      junto com os seus outros programas.
                    </p>
                    <button
                      className="btn btn-theme btn-lg w-100"
                      type="button"
                      onClick={instalar}
                      disabled={instalando}
                    >
                      <i className="bi bi-download me-2"></i>
                      {instalando ? 'Instalando…' : 'Instalar neste computador'}
                    </button>
                  </>
                ) : (
                  <>
                    {/* Sem o convite do Chrome: ou já foi instalado, ou o
                        navegador não instala (Firefox). O caminho manual do
                        Chrome/Edge sempre existe. */}
                    <p className="mb-2">Pelo próprio navegador:</p>
                    <Passos
                      itens={[
                        <>
                          No <strong>Chrome</strong> ou no <strong>Edge</strong>,
                          procure o ícone <i className="bi bi-display"></i> na
                          ponta direita da barra de endereço
                        </>,
                        <>
                          Não achou? Abra os <strong>três pontinhos</strong> (⋮)
                          e vá em <strong>Transmitir, salvar e compartilhar</strong>{' '}
                          → <strong>Instalar página como app</strong>
                        </>,
                        <>Confirme</>,
                      ]}
                    />
                    <p className="small text-secondary mb-0">
                      No Firefox não dá para instalar — use o Chrome ou o Edge.
                    </p>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <div className="text-center mt-4">
        <a className="btn btn-link" href="/inicio">
          Ir para o app
        </a>
      </div>
    </div>
  );
}

/** Passos numerados — o formato que se acompanha com o celular na mão. */
function Passos({ itens }: { itens: React.ReactNode[] }) {
  return (
    <ol className="list-unstyled mb-3">
      {itens.map((item, i) => (
        <li key={i} className="d-flex gap-2 mb-2">
          <span
            className="badge rounded-circle bg-theme-1 flex-shrink-0 d-flex align-items-center justify-content-center"
            style={{ width: 24, height: 24 }}
          >
            {i + 1}
          </span>
          <span>{item}</span>
        </li>
      ))}
    </ol>
  );
}
